import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export const EXTRA_ROLES = [
  ["is_kit_admin", "Kit Admin"], ["is_expense_approver", "Expense Approver"], ["is_treasurer", "Treasurer"],
  ["is_welfare_officer", "Welfare Officer"], ["is_club_secretary", "Club Secretary"],
];
const AGE_GROUPS = ["Adults","U18","U17","U16","U15","U14","U13","U12","U11","U10","U9","U8","U7","U6"];

const F = ({ label, children }) => <div><Label className="text-[10px] tracking-widest uppercase opacity-70">{label}</Label><div className="mt-1">{children}</div></div>;

export function EditUserDialog({ user, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    name: user.name || "", email: user.email || "", phone: user.phone || "", fan_number: user.fan_number || "",
    date_of_birth: user.date_of_birth || "", emergency_contact_name: user.emergency_contact_name || "", emergency_contact_phone: user.emergency_contact_phone || "",
    role: user.role || "member", year_group_lead_ages: user.year_group_lead_ages || [],
    ...Object.fromEntries(EXTRA_ROLES.map(([k]) => [k, user[k] === true])),
  }));
  const [busy, setBusy] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleIds, setRoleIds] = useState(user.role_ids || []);
  useEffect(() => { api.get("/admin/roles").then(r => setRoles(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const emailChanged = f.email.trim().toLowerCase() !== (user.email || "").toLowerCase();

  const save = async () => {
    if (!f.name.trim() || !f.email.trim()) { toast.error("Name and email are required"); return; }
    if (emailChanged && !window.confirm(`Change ${user.name}'s sign-in email to ${f.email.trim()}?\n\nThey'll be emailed at both the old and new address.`)) return;
    setBusy(true);
    try {
      const flags = Object.fromEntries(EXTRA_ROLES.map(([k]) => [k, user[k] === true]));
      const { data } = await api.put(`/admin/users/${user.id}/profile`, { ...f, ...flags, date_of_birth: f.date_of_birth || null });
      if (JSON.stringify([...roleIds].sort()) !== JSON.stringify([...(user.role_ids || [])].sort())) await api.put(`/admin/users/${user.id}/roles`, { role_ids: roleIds });
      toast.success(data.changed?.length ? `Saved · ${data.changed.join(", ")}${data.notified?.length ? ` · notified ${data.notified.length} address${data.notified.length === 1 ? "" : "es"}` : ""}` : "No changes");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl">Edit user</DialogTitle>
          <DialogDescription>Contact details, sign-in email, role and extra permissions. Every change is audited.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Name"><Input value={f.name} onChange={e => set("name", e.target.value)} data-testid="edit-user-name" /></F>
            <F label="Sign-in email"><Input type="email" value={f.email} onChange={e => set("email", e.target.value)} data-testid="edit-user-email" className={emailChanged ? "border-amber-400/60" : ""} /></F>
            <F label="Phone"><Input value={f.phone} onChange={e => set("phone", e.target.value)} data-testid="edit-user-phone" /></F>
            <F label="FAN number"><Input value={f.fan_number} onChange={e => set("fan_number", e.target.value)} placeholder="FA number" data-testid="edit-user-fan" /></F>
            <F label="Date of birth (DBS)"><Input type="date" value={f.date_of_birth} onChange={e => set("date_of_birth", e.target.value)} data-testid="edit-user-dob" /></F>
            <F label="Role">
              <Select value={f.role} onValueChange={v => set("role", v)}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem><SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="member">Member</SelectItem><SelectItem value="vendor">Vendor (view-only calendar)</SelectItem><SelectItem value="treasurer">Treasurer (finance only)</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Emergency contact"><Input value={f.emergency_contact_name} onChange={e => set("emergency_contact_name", e.target.value)} placeholder="Name" data-testid="edit-user-ec-name" /></F>
            <F label="Emergency contact phone"><Input value={f.emergency_contact_phone} onChange={e => set("emergency_contact_phone", e.target.value)} data-testid="edit-user-ec-phone" /></F>
          </div>
          {emailChanged && <div className="text-xs text-amber-300 border border-amber-400/40 bg-amber-500/10 rounded px-3 py-2" data-testid="edit-user-email-warning">Email change: {user.email} → {f.email.trim()}. Both addresses will be notified; the user signs in with the new one.</div>}
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Roles <span className="opacity-50 normal-case tracking-normal">· manage in Admin → People → Roles</span></Label>
            <div className="flex flex-wrap gap-2 mt-1.5" data-testid="edit-user-extra-roles">
              {roles.map(r => {
                const on = roleIds.includes(r.id);
                return <button key={r.id} type="button" onClick={() => setRoleIds(on ? roleIds.filter(x => x !== r.id) : [...roleIds, r.id])} data-testid={`edit-user-role-${r.key}`} title={(r.permissions || []).join(", ")}
                  className={`px-3 py-1.5 rounded border text-[10px] tracking-widest uppercase ${on ? "border-[#83B68B] bg-[#83B68B]/15 text-[#83B68B]" : "border-border opacity-60 hover:opacity-100"}`}>{r.name}</button>;
              })}
              {roles.length === 0 && <span className="text-xs opacity-50">Loading roles…</span>}
            </div>
            {f.role === "admin" && <div className="text-[11px] opacity-50 mt-1">Admins already have every permission — extra roles are only labels here.</div>}
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Year group lead</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="edit-user-ygl">
              {AGE_GROUPS.map(a => {
                const on = f.year_group_lead_ages.includes(a);
                return <button key={a} type="button" onClick={() => set("year_group_lead_ages", on ? f.year_group_lead_ages.filter(x => x !== a) : [...f.year_group_lead_ages, a])}
                  className={`px-2 py-1 rounded border text-[10px] tracking-widest uppercase ${on ? "border-[#83B68B] bg-[#83B68B]/15 text-[#83B68B]" : "border-border opacity-60 hover:opacity-100"}`}>{a}</button>;
              })}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded border border-border text-[10px] tracking-widest uppercase">Cancel</button>
            <button type="button" onClick={save} disabled={busy} data-testid="edit-user-save" className="btn-volt px-5 py-2 rounded text-[10px] tracking-widest uppercase disabled:opacity-40">{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
